import React, { useState, useEffect } from 'react';
import { ChevronUp } from 'lucide-react';
import './legal.css';

function PrivacyPolicy() {
  const [showScrollBtn, setShowScrollBtn] = useState(false);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    // Remover el loader inicial
    document.getElementById('initial-loader')?.remove();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowScrollBtn(true);
      } else {
        setShowScrollBtn(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="legal-container">
      <div className="legal-content">
        <div className="legal-header">
          <h1>Política de Privacidad</h1>
          <p className="last-updated">Última actualización: 17 de mayo de 2026</p>
        </div>

        <section className="legal-section">
          <h2>1. Introducción</h2>
          <p>
            En Turnlink ("nosotros", "nuestro" o "la Plataforma"), protegemos tu privacidad seriamente. 
            Esta Política de Privacidad explica cómo recopilamos, usamos, compartimos y protegemos tus datos personales 
            cuando utilizas nuestros servicios.
          </p>
        </section>

        <section className="legal-section">
          <h2>2. Información que Recopilamos</h2>
          <h3>2.1 Información que proporcionas directamente:</h3>
          <ul>
            <li><strong>Cuenta y Perfil:</strong> Nombre, correo electrónico, número de teléfono, dirección del negocio</li>
            <li><strong>Información del Negocio:</strong> Nombre del negocio, servicios ofrecidos, horarios, precios</li>
            <li><strong>Integraciones:</strong> Tokens de acceso de Google Calendar y WhatsApp</li>
            <li><strong>Datos de Citas:</strong> Nombres de clientes, números de teléfono, detalles de servicios, horarios</li>
            <li><strong>Comunicación:</strong> Mensajes, comentarios y soporte que nos envías</li>
          </ul>

          <h3>2.2 Información recopilada automáticamente:</h3>
          <ul>
            <li><strong>Datos de Dispositivo:</strong> Tipo de navegador, dirección IP, sistema operativo</li>
            <li><strong>Datos de Uso:</strong> Páginas visitadas, tiempo de permanencia, clics, búsquedas</li>
            <li><strong>Cookies:</strong> Para recordar preferencias y autenticación</li>
            <li><strong>Datos de Ubicación:</strong> Si lo permites, para mejorar servicios locales</li>
          </ul>

          <h3>2.3 Información de Terceros:</h3>
          <ul>
            <li>Google (calendarios, contactos y autorización OAuth)</li>
            <li>WhatsApp (para integración de mensajería)</li>
            <li>Proveedores de pago (para procesamiento de pagos)</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>3. Cómo Usamos Tu Información</h2>
          <p>Utilizamos los datos recopilados para:</p>
          <ul>
            <li>Proporcionar, mantener y mejorar nuestros servicios</li>
            <li>Procesar citas y gestionar calendarios</li>
            <li>Enviar notificaciones por WhatsApp y correo electrónico</li>
            <li>Autenticar usuarios y mantener seguridad</li>
            <li>Analizar uso y tendencias para optimizar la plataforma</li>
            <li>Cumplir con obligaciones legales</li>
            <li>Prevenir fraude y abuso</li>
            <li>Proporcionar soporte técnico</li>
            <li>Enviar actualizaciones de cuenta y avisos</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>4. Uso de Google Calendar y Datos Personales</h2>
          <p>
            Cuando autorizas acceso a Google Calendar, recopilamos:
          </p>
          <ul>
            <li>Tu identificación de Google</li>
            <li>Eventos del calendario (fechas, horas, participantes)</li>
            <li>Información de disponibilidad</li>
          </ul>
          <p>
            <strong>Importante:</strong> Solo usamos esta información para gestionar citas en tu negocio. 
            Nunca vendemos, compartimos ni usamos estos datos para otros propósitos.
          </p>
        </section>

        <section className="legal-section">
          <h2>5. Integración con WhatsApp</h2>
          <p>
            La integración de WhatsApp permite:
          </p>
          <ul>
            <li>Enviar confirmaciones de citas</li>
            <li>Recordatorios de eventos</li>
            <li>Notificaciones de cambios en citas</li>
          </ul>
          <p>
            Los números de teléfono se procesan según las políticas de privacidad de WhatsApp. 
            Nos comprometemos a usar esta información solo para notificaciones relacionadas con citas.
          </p>
        </section>

        <section className="legal-section">
          <h2>6. Compartición de Información</h2>
          <p>No vendemos tu información personal. Sin embargo, compartimos datos con:</p>
          <ul>
            <li><strong>Proveedores de Servicio:</strong> Google, WhatsApp y plataformas de hosting (solo lo necesario)</li>
            <li><strong>Cumplimiento Legal:</strong> Cuando la ley lo requiere o para proteger derechos</li>
            <li><strong>Adquisiciones:</strong> Si Turnlink es adquirida, tus datos pasarían al nuevo propietario</li>
            <li><strong>Con tu Consentimiento:</strong> Cuando explícitamente lo autorices</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>7. Seguridad de Datos</h2>
          <p>
            Implementamos medidas de seguridad técnicas y organizacionales:
          </p>
          <ul>
            <li>Encriptación de datos en tránsito (HTTPS/TLS)</li>
            <li>Encriptación de datos sensibles en reposo</li>
            <li>Autenticación segura (JWT, OAuth 2.0)</li>
            <li>Acceso restringido a bases de datos</li>
            <li>Auditorías de seguridad regulares</li>
            <li>Monitoreo de amenazas</li>
          </ul>
          <p>
            <strong>Nota:</strong> Aunque implementamos medidas robustas, ningún sistema es 100% seguro. 
            Reporta brechas de seguridad a: <strong>soporte@turnlink.app</strong>
          </p>
        </section>

        <section className="legal-section">
          <h2>8. Retención de Datos</h2>
          <ul>
            <li><strong>Datos de Cuenta:</strong> Mientras la cuenta esté activa</li>
            <li><strong>Datos de Citas:</strong> Por 2 años después de la cita (para histórico y análisis)</li>
            <li><strong>Logs de Sistema:</strong> Por 90 días</li>
            <li><strong>Datos Eliminados:</strong> Pueden retenerse en backups por hasta 30 días</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>9. Tus Derechos (Derechos ARCO)</h2>
          <p>Conforme a la Ley Federal de Protección de Datos Personales en Posesión de Particulares (LFPDPPP), tienes derecho a:</p>
          <ul>
            <li><strong>Acceso (A):</strong> Solicitar acceso a tus datos personales y saber cómo los usamos</li>
            <li><strong>Rectificación (R):</strong> Corregir, actualizar o enmendar datos inexactos o incompletos</li>
            <li><strong>Cancelación (C):</strong> Solicitar la eliminación de tus datos cuando ya no sean necesarios</li>
            <li><strong>Oposición (O):</strong> Oponerme al procesamiento de mis datos para fines específicos</li>
          </ul>
          <p>
            <strong>Cómo ejercer tus derechos ARCO:</strong> Envía solicitud a <strong>soporte@turnlink.app</strong> con:
          </p>
          <ul>
            <li>Tu nombre completo e identificación</li>
            <li>Descripción clara del derecho que deseas ejercer</li>
            <li>Prueba de identidad (copia de INE o documento equivalente)</li>
          </ul>
          <p>
            Responderemos tu solicitud dentro de 20 días hábiles según la ley mexicana.
          </p>
        </section>

        <section className="legal-section">
          <h2>10. Cookies</h2>
          <p>
            Utilizamos cookies para:
          </p>
          <ul>
            <li>Mantener sesión activa</li>
            <li>Recordar preferencias</li>
            <li>Mejorar experiencia del usuario</li>
            <li>Análisis de uso</li>
          </ul>
          <p>
            Puedes desactivar cookies en tu navegador, pero esto puede afectar la funcionalidad de la plataforma.
          </p>
        </section>

        <section className="legal-section">
          <h2>11. Menores de Edad</h2>
          <p>
            Turnlink no está dirigida a menores de 18 años. No recopilamos intencionalmente datos de menores. 
            Si descubrimos que hemos recopilado datos de un menor, los eliminaremos inmediatamente.
          </p>
        </section>

        <section className="legal-section">
          <h2>12. Cambios en esta Política</h2>
          <p>
            Podemos actualizar esta política ocasionalmente. Te notificaremos de cambios significativos 
            por correo o mediante un aviso en la plataforma. Tu uso continuado implica aceptación.
          </p>
        </section>

        <section className="legal-section">
          <h2>13. Contacto</h2>
          <p>Si tienes preguntas sobre privacidad:</p>
          <ul>
            <li>Email: <strong>soporte@turnlink.app</strong></li>
            <li>Sitio: <strong>www.turnlink.app</strong></li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>14. Cumplimiento Legal (México)</h2>
          <p>
            Turnlink cumple con la <strong>Ley Federal de Protección de Datos Personales en Posesión de Particulares (LFPDPPP)</strong> 
            y su Reglamento. Esta plataforma opera bajo las leyes mexicanas de protección de datos y privacidad.
          </p>
        </section>
      </div>

      <button 
        className={`scroll-to-top-btn ${showScrollBtn ? 'show' : ''}`}
        onClick={scrollToTop}
        title="Volver al inicio"
        style={{ display: showScrollBtn ? 'flex' : 'none' }}
      >
        <ChevronUp size={24} />
      </button>
    </div>
  );
}

export default PrivacyPolicy;
